// SPDX-License-Identifier: AGPL-3.0-or-later

import {BLUESKY_PROVIDER_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {DOMAIN_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {type ConnectionType, ConnectionTypes, OAUTH_CONNECTION_TYPES} from '@fluxer/constants/src/ConnectionConstants';
import type {I18n} from '@lingui/core';

export type ProviderConnectionType = Exclude<ConnectionType, typeof ConnectionTypes.DOMAIN>;

export const CONNECTION_PROVIDER_NAMES: Record<ProviderConnectionType, string> = {
	[ConnectionTypes.BLUESKY]: BLUESKY_PROVIDER_NAME,
	[ConnectionTypes.STEAM]: 'Steam',
	[ConnectionTypes.TWITCH]: 'Twitch',
	[ConnectionTypes.KICK]: 'Kick',
	[ConnectionTypes.BATTLENET]: 'Battle.net',
	[ConnectionTypes.YOUTUBE]: 'YouTube',
};

interface ConnectionIdentity {
	type: string;
	id: string;
	name: string;
}

const STEAM_ID = /^\d{17}$/;
const YOUTUBE_CHANNEL_ID = /^UC[\w-]{22}$/;
const TWITCH_LOGIN = /^\w{1,25}$/;
const KICK_USERNAME = /^[\w-]{1,32}$/;

const PROFILE_URLS: Record<string, (connection: ConnectionIdentity) => string | null> = {
	[ConnectionTypes.BLUESKY]: ({name}) => `https://bsky.app/profile/${name}`,
	[ConnectionTypes.DOMAIN]: ({name}) => `https://${name}`,
	[ConnectionTypes.STEAM]: ({id}) => (STEAM_ID.test(id) ? `https://steamcommunity.com/profiles/${id}` : null),
	[ConnectionTypes.YOUTUBE]: ({id}) => (YOUTUBE_CHANNEL_ID.test(id) ? `https://www.youtube.com/channel/${id}` : null),
	[ConnectionTypes.TWITCH]: ({name}) => (TWITCH_LOGIN.test(name) ? `https://www.twitch.tv/${name}` : null),
	[ConnectionTypes.KICK]: ({name}) => (KICK_USERNAME.test(name) ? `https://kick.com/${name}` : null),
};

export function getConnectionProviderName(i18n: I18n, type: string): string {
	return Object.hasOwn(CONNECTION_PROVIDER_NAMES, type)
		? CONNECTION_PROVIDER_NAMES[type as ProviderConnectionType]
		: i18n._(DOMAIN_DESCRIPTOR);
}

export function getConnectionProfileUrl(connection: ConnectionIdentity): string | null {
	return Object.hasOwn(PROFILE_URLS, connection.type) ? PROFILE_URLS[connection.type](connection) : null;
}

export function getAddableConnectionTypes(
	blueskyEnabled: boolean,
	connectionTypes: ReadonlyArray<string>,
): Array<ConnectionType> {
	return [
		...(blueskyEnabled ? [ConnectionTypes.BLUESKY] : []),
		...OAUTH_CONNECTION_TYPES.filter((type) => connectionTypes.includes(type)),
		ConnectionTypes.DOMAIN,
	];
}
